import { FileText, FileUp, Lightbulb } from "lucide-react";
import { Link } from "react-router-dom";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

import type { AcqScene, AcqSceneRefDoc } from "../../api/acqService";

/**
 * 「从你的资料里读出的获客场景」(契约 §16.1)。
 *
 * ★★ 老板原话:「你是按照先拿到知识库,再读用户产品,然后再对应找到行业的场景来做推荐的么,
 *   你是不是偷懒了」。上一版推荐的底是 **007 平台优先级 + KB 只能升降一档** ——
 *   中间**没有显式的「场景」层**,所以一份知产味的全局排序照搬给了每个行业。
 *   这一条就是那一层露出来的样子:推荐的理由先摆出来,然后才是 17 张卡。
 * ★ 点一枚 chip **高亮引用它的卡**(再点取消)—— 它回答的是"这个场景到底要我去哪儿找",
 *   而那正是一个新用户看完场景之后的下一个念头。**不做筛**(不把别的卡藏起来):
 *   藏起来的话他看不到"这个场景只有一条线抓得到"这件事,而那恰恰是要紧的。
 * ★★ `scenes` 为空**整块不渲染**,但 KB 为空时要**说一句** —— 两者长得一样而原因完全不同:
 *   一个是"这个租户的资料推不出场景"(还能再推),另一个是"根本没有资料"(先去传)。
 *   合成一块空白的后果是用户对着一页灰卡以为功能坏了(实测 33 个租户绝大多数 KB=0)。
 */
export function SceneStrip({
  scenes,
  kbDocs,
  activeId,
  onPick,
  countOf,
  productName,
}: {
  scenes: AcqScene[];
  /** 知识库篇数(`inferredFrom.kbDocs`)。0 = 那句「先传资料」的判据。 */
  kbDocs: number;
  activeId: string | null;
  onPick: (id: string | null) => void;
  /** 这个场景对应几条线(目录里真被它引用的卡数)。 */
  countOf: (scene: AcqScene) => number;
  /** `node_code` → 产品名。取不到回落到 code 本身。 */
  productName: (code: string | null | undefined) => string;
}) {
  if (scenes.length === 0) {
    if (kbDocs > 0) return null;
    return (
      <section className="rounded-xl border border-dashed bg-muted/30 px-4 py-3">
        <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <FileUp className="h-3.5 w-3.5 shrink-0" />
          知识库为空，推不出场景；先传资料再重新推断。
          <Link to="/knowledge" className="font-semibold text-primary hover:underline">
            去传资料
          </Link>
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border bg-card px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <h2 className="flex items-center gap-1.5 text-sm font-bold">
          <Lightbulb className="h-4 w-4 text-primary" />
          从你的资料里读出的获客场景
        </h2>
        <span className="text-2xs text-muted-foreground">
          下面那些「去哪里找」，是按这几个场景挑的 —— 点一个看它对应哪几条线
        </span>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-2">
        {scenes.map((s) => {
          const on = activeId === s.id;
          const n = countOf(s);
          const product = s.productCode ? productName(s.productCode) : null;
          const docs = s.refDocs ?? [];
          return (
            <div
              key={s.id}
              className={cn(
                "inline-flex max-w-full items-center rounded-full border transition-colors",
                on
                  ? "border-primary bg-primary/10 text-primary"
                  : "bg-background hover:border-primary/50 hover:bg-primary/5",
                // ★ weak = 「材料不足,AI 猜的」(契约 §17.2):灰一档,和有证据的场景分开。
                s.weak && !on && "border-dashed opacity-75"
              )}
            >
              <button
                type="button"
                aria-pressed={on}
                onClick={() => onPick(on ? null : s.id)}
                title={[s.who, s.moment].filter(Boolean).join(" · ") || undefined}
                className="min-w-0 rounded-full px-3 py-1.5 text-left text-2xs"
              >
                <span className="font-semibold">{s.title}</span>
                {/* ★ 产品名跟在场景后面:场景是"谁在什么时刻",产品是"要我卖什么" ——
                    两样都写出来,用户才对得上自己的产品线。推不出产品的场景不编一个。 */}
                {product && <span className="ml-1.5 text-muted-foreground">· {product}</span>}
                {/* ★ 「对应 N 条线」**0 也照报**:一个推出来却一条线都抓不到的场景,
                    正是该让人看见的东西(它说明这个行业包还缺料),藏起来等于替它遮丑。 */}
                <span className={cn("ml-1.5", on ? "opacity-80" : "text-muted-foreground")}>
                  · 对应 {n} 条线
                </span>
                {/* ★★ 契约 §17.2:猜出来的场景要标出来,而且它**不参与定档** ——
                    不标的话用户会把它当成"AI 从我的资料里读出来的",照着它花钱。 */}
                {s.weak && (
                  <span
                    className="ml-1.5 text-muted-foreground"
                    title="这个产品的资料里没读到对应段落，这条是 AI 猜的；它不参与推荐档"
                  >
                    · 材料不足，AI 猜的
                  </span>
                )}
              </button>
              {/* ── 出处(契约 §17.3.5):点开列文档名,点文档去知识库 ──────────── */}
              {docs.length > 0 && <SceneRefDocs title={s.title} docs={docs} />}
            </div>
          );
        })}
      </div>
      {activeId != null && (
        <p className="mt-2 text-2xs text-muted-foreground">
          下面亮起来的卡就是这个场景抓得到的线。
          <button
            type="button"
            onClick={() => onPick(null)}
            className="ml-1 font-semibold text-primary hover:underline"
          >
            取消高亮
          </button>
        </p>
      )}
    </section>
  );
}

/**
 * 场景的出处(契约 §17.3.5:「场景 chip 点开小浮层列出处文档名,点文档跳知识库文档页」)。
 *
 * ★★ 它回答的是这一版推荐里最贵的那个问题:**「AI 凭什么这么说」**。
 *   `why` 里写着「场景「…」(K1)」—— 而 `K1` 对用户毫无意义,他要看的是哪一篇文档。
 * ★ 落地是 `/knowledge?q=<文档名>`:知识库那一页的搜索框**本来就绑在 `q` 上**
 *   (`useUrlParams`),所以点过去看到的就是过滤到这一篇的列表。
 *   ★ 没有"单篇文档页"这条路由(知识库是一页带弹框),所以这里**不编一个 `/knowledge/:id`**
 *   —— 编出来的链接会落到 404,而那比多一步搜索坏得多。
 * ★ 触发器是**与高亮分开的一个按钮**:点 chip 本体是"高亮引用它的卡",
 *   点这枚小图标是"看出处"。合成一个的话,想看出处的人每次都会顺手改掉高亮。
 */
function SceneRefDocs({ title, docs }: { title: string; docs: AcqSceneRefDoc[] }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`「${title}」是从哪几篇资料读出来的`}
          title={`出处 ${docs.length} 篇`}
          className="mr-1 inline-flex items-center gap-0.5 rounded-full px-1.5 py-1 text-2xs text-muted-foreground hover:bg-primary/10 hover:text-primary"
        >
          <FileText className="h-3 w-3" />
          {docs.length}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        <p className="text-2xs text-muted-foreground">这条场景是从这几篇资料里读出来的</p>
        <ul className="mt-2 space-y-1.5">
          {docs.map((d) => (
            <li key={`${d.docId}-${d.chunkId ?? ""}`}>
              <Link
                to={`/knowledge?q=${encodeURIComponent(d.docName)}`}
                className="block truncate text-xs font-semibold text-primary hover:underline"
                title={d.docName}
              >
                {d.docName}
              </Link>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
